import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

/* ============================================================
   FONTS — add these once, e.g. in your Next.js _app or layout:
   <link rel="preconnect" href="https://fonts.googleapis.com" />
   <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
   Then reference them as font-display / font-sans below (see tailwind.config
   fontFamily extension: display: ['"Cormorant Garamond"','serif'], sans: ['Inter','sans-serif'])
   ============================================================ */

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) return resolve(true);
    const script = document.createElement("script");
    script.id = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const API_BASE = "https://bhavanaastro.onrender.com/api/ganpati-booking";

/* ============================================================
   CONFIG — pricing, dates, muhurat data
   ============================================================ */

const STHAPANA_DATE_ISO = "2026-09-14";
const STHAPANA_DATE_LABEL = "Monday, 14 September 2026";

// Slot-hold fee — includes the materials checklist.
// USD is a flat price, not a computed conversion from INR.
const HOLD_FEE_INR = 51;
const HOLD_FEE_USD = 6;

// TODO: confirm real full-session prices. Shown separately & clearly,
// never hidden, per the brief. USD values are flat, independently
// priced — not derived from the INR figures.
const FULL_PRICE_INR = { live: 1101, recorded: 701 };
const FULL_PRICE_USD = { live: 25, recorded: 15 };

// TODO: replace with real muhurat windows for the sthapana date,
// ideally pulled from a panchang API. Structure kept per-date so this
// scales if you ever support more than one date.
const MUHURAT_SLOTS = {
  [STHAPANA_DATE_ISO]: {
    morning: [
      { id: "m1", time: "6:00 – 7:30 AM", availableCount: 3, recommended: true },
      { id: "m2", time: "7:30 – 9:00 AM", availableCount: 5, recommended: false },
      { id: "m3", time: "9:00 – 10:30 AM", availableCount: 2, recommended: false },
    ],
    afternoon: [
      { id: "a1", time: "12:00 – 1:30 PM", availableCount: 4, recommended: false },
      { id: "a2", time: "1:30 – 3:00 PM", availableCount: 6, recommended: false },
    ],
  },
};

// Evening slots are for aarti/visarjan-type functions, not sthapana —
// kept visually and functionally separate so no one books a sthapana
// slot outside the actual muhurat window.
const OTHER_FUNCTION_SLOTS = [
  { id: "e1", label: "Evening Aarti", time: "6:30 – 7:30 PM" },
];

const TIMEZONE_LABEL = "IST (India Standard Time, UTC+5:30)";

const COUNTRY_CODES = [
  { code: "+91", label: "India (+91)" },
  { code: "+1", label: "US/Canada (+1)" },
  { code: "+44", label: "UK (+44)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+971", label: "UAE (+971)" },
  { code: "+65", label: "Singapore (+65)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+27", label: "South Africa (+27)" },
];

// TODO: replace with real, verified reviews. Do not ship this section
// with placeholder claims and no evidence — the brief is explicit
// about this.
const TESTIMONIALS = [
  {
    name: "Placeholder — add a real reviewer",
    quote: "Add a real testimonial here once you have verified reviews.",
    rating: 5,
  },
];
const AGGREGATE_RATING = { value: 0, count: 0 }; // TODO: wire to real data

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function AstroSevenGanpatiLanding() {
  const [mode, setMode] = useState("live"); // "live" | "recorded"
  const [sessionPrivacy, setSessionPrivacy] = useState("");
  const [sessionLanguage, setSessionLanguage] = useState("hindi"); // "hindi" | "marathi" | "english"
  const [showForm, setShowForm] = useState(false);
  const [formStep, setFormStep] = useState(1); // 1 Session, 2 Time, 3 Details, 4 Summary/Pay
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  const [isIndia, setIsIndia] = useState(true);
  const [manualCurrency, setManualCurrency] = useState(null); // user override via toggle

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const { data } = await axios.get("https://ipapi.co/json/");
        setIsIndia(data?.country_code === "IN");
      } catch (err) {
        console.log("Country detection failed, defaulting to India:", err);
        setIsIndia(true);
      }
    };
    detectCountry();
  }, []);

  useEffect(() => {
    const heroEl = document.getElementById("hero-cta-anchor");
    if (!heroEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setScrolledPastHero(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(heroEl);
    return () => observer.disconnect();
  }, []);

  const showINR = manualCurrency ? manualCurrency === "INR" : isIndia;
  const currency = showINR ? "INR" : "USD";
  const currencySymbol = showINR ? "₹" : "$";

  const holdFeeDisplay = showINR ? `₹${HOLD_FEE_INR}` : `$${HOLD_FEE_USD}`;
  const holdFeeSubDisplay = showINR ? `$${HOLD_FEE_USD}` : `₹${HOLD_FEE_INR}`;

  const fullPriceInr = FULL_PRICE_INR[mode];
  const fullPriceUsd = FULL_PRICE_USD[mode];
  const fullPriceDisplay = showINR ? `₹${fullPriceInr}` : `$${fullPriceUsd}`;

  const formRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    countryCode: "+91",
    phone: "",
    email: "",
    timeSlotId: "",
    notes: "",
  });
  const [touched, setTouched] = useState({});

  const dateSlots = MUHURAT_SLOTS[STHAPANA_DATE_ISO] || { morning: [], afternoon: [] };
  const allLiveSlots = [...dateSlots.morning, ...dateSlots.afternoon];
  const selectedSlot = allLiveSlots.find((s) => s.id === form.timeSlotId);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleBlur = (field) => setTouched({ ...touched, [field]: true });

  const phoneDigits = form.phone.replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 7 && phoneDigits.length <= 12;
  const nameValid = form.name.trim().length >= 2;

  const handleBookNowClick = () => {
    setShowForm(true);
    setFormStep(1);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  // Single handler for every "Book" trigger on the page (hero, floating
  // button). If the form is already open, this just scrolls down to it
  // instead of resetting anything — so the person never has to scroll
  // back to the top to keep booking.
  const handleFloatingCtaClick = () => {
    if (showForm) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      handleBookNowClick();
    }
  };

  const canAdvanceFromStep = (step) => {
    if (step === 1) return mode === "recorded" || !!sessionPrivacy;
    if (step === 2) return mode === "recorded" || !!form.timeSlotId;
    if (step === 3) return nameValid && phoneValid;
    return true;
  };

  const goToStep = (step) => {
    setError("");
    if (step > formStep && !canAdvanceFromStep(formStep)) {
      setError("Please complete this step before continuing.");
      return;
    }
    setFormStep(step);
  };

  const handleFinalSubmit = async () => {
    setError("");
    if (!nameValid || !phoneValid) {
      setError("Please check your name and phone number.");
      setFormStep(3);
      return;
    }
    if (mode === "live" && !sessionPrivacy) {
      setError("Please choose a private or public session.");
      setFormStep(1);
      return;
    }
    if (mode === "live" && !form.timeSlotId) {
      setError("Please pick a time slot.");
      setFormStep(2);
      return;
    }

    setLoading(true);

    
    try {
      if (showINR) {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          setError("Failed to load Razorpay. Check your internet connection.");
          setLoading(false);
          return;
        }

        const res = await axios.post(`${API_BASE}/create-order`, {
          name: form.name,
          phone: `${form.countryCode}${form.phone}`,
          email: form.email,
          sessionMode: mode,
          sessionPrivacy: mode === "live" ? sessionPrivacy : undefined,
          sessionLanguage,
          sthapanaDate: STHAPANA_DATE_ISO,
          timeSlot: mode === "live" ? selectedSlot?.time : "Anytime (recorded)",
          notes: form.notes,
          amount: HOLD_FEE_INR,
          currency: "INR",
        });

        const { orderId, razorpayOrderId, keyId, amount, currency: orderCurrency } = res.data;
        if (!razorpayOrderId) throw new Error("Payment order not received");

        const options = {
          key: keyId,
          amount,
          currency: orderCurrency || "INR",
          name: "AstroSeven",
          description: `Ganpati Sthapana Pooja – ${mode === "live" ? "Live" : "Recorded"}`,
          order_id: razorpayOrderId,
          prefill: { name: form.name, email: form.email || "", contact: `${form.countryCode}${form.phone}` },
          theme: { color: "#8A2432" },
          handler: async function (response) {
            try {
              setLoading(true);
              await axios.post(`${API_BASE}/verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                orderId,
              });
              setSuccess(true);
            } catch (err) {
              console.error(err);
              setError("Payment succeeded but verification failed. Please contact support with your phone number.");
            } finally {
              setLoading(false);
            }
          },
          modal: { ondismiss: () => { setLoading(false); setError("Payment cancelled."); } },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (response) => {
          setError(`Payment failed: ${response.error.description}`);
          setLoading(false);
        });
        rzp.open();
        setLoading(false);
      } else {
        // International payment path — Stripe Checkout.
        const res = await axios.post(`${API_BASE}/create-stripe-session`, {
          name: form.name,
          phone: `${form.countryCode}${form.phone}`,
          email: form.email,
          sessionMode: mode,
          sessionPrivacy: mode === "live" ? sessionPrivacy : undefined,
          sessionLanguage,
          sthapanaDate: STHAPANA_DATE_ISO,
          timeSlot: mode === "live" ? selectedSlot?.time : "Anytime (recorded)",
          notes: form.notes,
          amountUsd: HOLD_FEE_USD,
        });
        if (res.data?.checkoutUrl) {
          window.location.href = res.data.checkoutUrl;
        } else {
          throw new Error("Stripe session not received");
        }
      }
    } catch (err) {
      console.error(err);
      setError("Failed to start booking. Please try again.");
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF3E7] px-6 font-sans">
        <div className="max-w-md w-full text-center bg-white border border-[#E9D9BD] rounded-3xl shadow-xl p-10">
          <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-[#8A2432] flex items-center justify-center">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M4 12.5L9.5 18L20 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="text-3xl font-display font-bold text-gray-900 mb-3">Your booking is confirmed</h1>
          <p className="text-gray-600 leading-relaxed">
            Thank you, {form.name}. Your {mode === "live" ? `live (${sessionPrivacy})` : "recorded"} Ganpati
            Sthapana session is booked for <strong>{STHAPANA_DATE_LABEL}</strong>
            {mode === "live" && selectedSlot && (
              <> at <strong>{selectedSlot.time} {TIMEZONE_LABEL}</strong></>
            )}
            . We'll send a confirmation message to <strong>{form.countryCode} {form.phone}</strong> shortly,
            along with your pooja samagri checklist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FBF3E7] text-gray-800 font-sans relative">
      <MandalaBackgroundTexture />

      {/* ---------- 1. HERO ---------- */}
      <section className="relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl -z-0"
          style={{ background: "radial-gradient(circle, #F0B25C 0%, transparent 70%)" }}
        />

        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-14 grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <span className="inline-flex items-center gap-2 bg-[#F6E3C4] text-[#8A5A1F] px-4 py-1.5 rounded-full text-sm font-semibold">
              <DiyaIcon /> {STHAPANA_DATE_LABEL}
            </span>

            <h1 className="mt-5 text-4xl md:text-6xl font-display font-bold text-gray-900 leading-[1.08]">
              Bring the pandit
              <br />
              to <span className="text-[#8A2432]">your Ganpati</span>
            </h1>

            <p className="mt-6 text-lg text-gray-600 max-w-md leading-relaxed">
              Good pandits get booked out fast in Ganpati season, and the ones still
              free often charge more than most families can spare. AstroSeven brings
              a verified pandit or guruji to guide your full sthapana vidhi — live
              over video, or through a recorded session.
            </p>

            <div id="hero-cta-anchor" className="mt-8">
              <div className="flex items-center gap-5 flex-wrap">
                <button
                  onClick={handleBookNowClick}
                  className="bg-[#8A2432] hover:bg-[#701D28] text-white px-8 py-3.5 rounded-full font-semibold shadow-lg shadow-[#8A2432]/20 transition-all hover:shadow-xl hover:-translate-y-0.5"
                >
                  Book for {holdFeeDisplay}
                </button>
                <CurrencyToggle
                  showINR={showINR}
                  onChange={(v) => setManualCurrency(v)}
                />
              </div>
              <p className="mt-3 text-sm text-gray-500 max-w-sm">
                {holdFeeDisplay} holds your slot and includes your full pooja samagri
                checklist. Full session price ({fullPriceDisplay}) is shown clearly
                before you pay anything further.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
            className="relative mx-auto w-full max-w-[360px]"
          >
            <div
              className="overflow-hidden bg-[#F6E3C4] w-full aspect-[3/4] shadow-2xl"
              style={{ borderRadius: "180px 180px 16px 16px" }}
            >
              <img
                src="/ganpati-sthapana-hero.png"
                alt="Pandit performing Ganpati Sthapana vidhi"
                className="w-full h-full object-cover"
              />
            </div>
            <div
              className="absolute -inset-2 -z-10"
              style={{ borderRadius: "190px 190px 20px 20px", border: "1.5px solid #C99A4A" }}
            />
            <MandalaFlourish className="absolute -bottom-6 -left-6 w-16 h-16 text-[#C99A4A] opacity-70" />
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-t border-b border-[#E9D9BD] bg-white/70">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap justify-center gap-x-10 gap-y-2 text-sm font-medium text-[#8A5A1F]">
          <span>Verified & experienced pandits</span>
          <span className="text-[#C99A4A]">•</span>
          <span>Fair, transparent pricing</span>
          <span className="text-[#C99A4A]">•</span>
          <span>Hindi · Marathi · English</span>
        </div>
      </section>

      {/* ---------- 2. HOW IT WORKS ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900">How it works</h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            Here's exactly what happens after you book — no surprises.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {[
            { step: "1", title: "Book your slot", text: `Pay ${holdFeeDisplay} to hold your slot for ${STHAPANA_DATE_LABEL}.` },
            { step: "2", title: "Confirmation message", text: "We send a confirmation message within a few hours to confirm your slot." },
            { step: "3", title: "Checklist sent", text: "Your full pooja samagri checklist arrives right after booking." },
            { step: "4", title: "Pandit assigned", text: "A verified pandit or guruji is matched to your session." },
            { step: "5", title: "Perform the vidhi", text: "Follow along step by step — every ritual explained plainly." },
          ].map((item) => (
            <div key={item.step} className="relative">
              <div className="w-9 h-9 rounded-full bg-[#8A2432] text-white text-sm font-bold flex items-center justify-center mb-4">
                {item.step}
              </div>
              <h3 className="font-display font-semibold text-lg text-gray-900 mb-1.5">{item.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- 3. LIVE VS RECORDED — real trade-offs ---------- */}
      <section className="bg-[#F6E3C4]/50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900">Choose how you'd like your session</h2>
            <p className="mt-3 text-gray-600">Both include the exact same complete vidhi.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <SessionCompareCard
              active={mode === "live"}
              onClick={() => setMode("live")}
              title="Live session"
              price={showINR ? `₹${FULL_PRICE_INR.live}` : `$${FULL_PRICE_USD.live}`}
              img="/ganpati-live-preview.png"
              pros={["Real-time guidance — ask questions as you go", "Feels closest to having a pandit in the room", "Fixed time slot on sthapana day"]}
              cons={["Needs a working camera/mic and a fixed time you can commit to", "Live slots are limited — the earlier you book, the more choice"]}
            />
            <SessionCompareCard
              active={mode === "recorded"}
              onClick={() => setMode("recorded")}
              title="Recorded session"
              price={showINR ? `₹${FULL_PRICE_INR.recorded}` : `$${FULL_PRICE_USD.recorded}`}
              img="/ganpati-recorded-preview.png"
              pros={["Play and pause at your own pace, any time on sthapana day", "No timezone or scheduling conflict — good default for non-IST hours", "Lower cost than a live slot"]}
              cons={["No real-time back-and-forth if something's unclear", "You're following a recording, not a live person watching you"]}
            />
          </div>
          <p className="mt-6 text-center text-sm text-gray-600 max-w-xl mx-auto">
            If you're outside IST-friendly hours, most international customers choose
            the recorded session so timing isn't a constraint.
          </p>
        </div>
      </section>

      {/* ---------- 4. TRUST — pandits, verification, testimonials ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-[0.8fr_1.2fr] gap-12 items-center">
        <div className="rounded-3xl overflow-hidden bg-[#F6E3C4] aspect-[4/5] shadow-xl">
          <img
            src="/ganpati-pandit-photo.png"
            alt="Pandit ji"
            className="w-full h-full object-cover"
            style={{ filter: "saturate(1.05) contrast(1.03)" }} // unify grade across all pandit photos
          />
        </div>
        <div>
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">Guided by pandits you can trust</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Every pandit and guruji on AstroSeven is verified before they're matched to a booking —
            experienced with Ganpati sthapana vidhi and comfortable guiding first-timers.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              "Identity and credentials verified before listing",
              "Experienced specifically with Ganpati sthapana vidhi",
              "Comfortable guiding people doing this for the first time",
              "Available in Hindi, Marathi, and English",
            ].map((t, i) => (
              <li key={i} className="flex gap-3 text-gray-700">
                <FaCheckDot />
                <span>{t}</span>
              </li>
            ))}
          </ul>

          <div className="border-t border-[#E9D9BD] pt-6">
            <div className="flex items-center gap-2 mb-4">
              <StarRating value={AGGREGATE_RATING.value} />
              <span className="text-sm text-gray-500">
                {AGGREGATE_RATING.count > 0
                  ? `${AGGREGATE_RATING.value.toFixed(1)} · ${AGGREGATE_RATING.count} reviews`
                  : "Reviews coming soon"}
              </span>
            </div>
            <div className="space-y-4">
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#E9D9BD] p-5">
                  <StarRating value={t.rating} size={13} />
                  <p className="text-sm text-gray-700 mt-2 leading-relaxed">"{t.quote}"</p>
                  <p className="text-xs text-gray-500 mt-2 font-medium">{t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 5. MATERIALS CHECKLIST TEASER ---------- */}
      <section className="py-20" style={{ background: "linear-gradient(180deg, #3D2140 0%, #2E1832 100%)" }}>
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <span className="inline-block text-xs font-semibold tracking-wide uppercase text-[#F0B25C] mb-3">
              Included with your {holdFeeDisplay} booking
            </span>
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              You'll know exactly what to arrange — nothing to guess
            </h2>
            <p className="text-[#E8D9EA] leading-relaxed mb-6">
              The moment you book, we send a complete pooja samagri checklist tailored to your
              sthapana — most items are things you can pick up locally or already have at home.
            </p>
            <ul className="space-y-2 text-[#E8D9EA] text-sm">
              {["Idol placement essentials (chowki, cloth, rangoli materials)", "Puja items (kalash, coconut, durva, flowers, incense)", "Modak/prasad basics", "A simple day-of checklist you can print or screenshot"].map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#F0B25C]">✦</span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-3xl shadow-2xl p-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Sample checklist preview</p>
            <div className="space-y-2.5">
              {["Chowki (wooden platform)", "Red/yellow cloth", "Kalash with mango leaves", "Coconut", "Durva grass, flowers", "Modak for naivedya"].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-gray-700 py-1.5 border-b border-gray-100 last:border-0">
                  <span className="w-4 h-4 rounded border-2 border-[#8A2432] flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 6. FAQ ---------- */}
      <section className="bg-[#F6E3C4]/50 py-20">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-gray-900 text-center mb-10">Common questions</h2>
          <div className="space-y-3">
            {[
              {
                q: `Is ${holdFeeDisplay} the full price?`,
                a: `No — ${holdFeeDisplay} holds your slot and includes your complete pooja samagri checklist. The full session price (${fullPriceDisplay} for a ${mode} session) is shown clearly in your booking summary, and confirmed again in your confirmation message before you pay anything further.`,
              },
              {
                q: "What exactly is included?",
                a: "A verified pandit or guruji, your complete sthapana vidhi guided step by step in Hindi, Marathi, or English, a confirmation message, and your materials checklist sent right after booking.",
              },
              {
                q: "I've never done Ganpati Sthapana myself — will I understand what to do?",
                a: "Yes. The pandit explains every step in plain language as you go — you don't need to know any mantras or rituals beforehand.",
              },
              {
                q: "What's the difference between a private and a public live session?",
                a: "A private session is just for your family, at your chosen slot. A public session is a shared live video call where a few other families join the same pandit at the same time — it's priced lower and tends to have earlier availability, but the pandit's attention is split across households.",
              },
              {
                q: "I'm outside India — how do time slots work for me?",
                a: `All slot times are shown in ${TIMEZONE_LABEL}. Convert to your local time when booking a live slot, or choose a recorded session, which most international customers pick since there's no fixed time to coordinate around.`,
              },
              {
                q: "What if I need to cancel or reschedule?",
                a: "Message our support team as soon as you know — we'll do our best to move your slot to a new time, or offer the closest available alternative within the sthapana window.",
              },
              {
                q: "How do I pay if I'm outside India?",
                a: "You can pay by card through our international checkout in USD, alongside the standard Razorpay option for customers in India.",
              },
            ].map((item, i) => (
              <FaqAccordionItem key={i} item={item} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- 7. BOOKING FORM ---------- */}
      {showForm && (
        <section ref={formRef} className="px-6 py-20">
          <div className="max-w-2xl mx-auto">
            <StepIndicator currentStep={formStep} totalSteps={4} labels={["Session", "Time", "Details", "Summary"]} />

            <div className="bg-white rounded-3xl shadow-xl border border-[#E9D9BD] mt-6 relative overflow-hidden">
              {loading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <div className="w-10 h-10 border-2 border-[#8A2432] border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {/* STEP 1 — Session choice */}
              {formStep === 1 && (
                <div className="p-8 sm:p-10 bg-[#FDF8F0]">
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-1">Choose your session</h2>
                  <p className="text-sm text-gray-500 mb-6">{STHAPANA_DATE_LABEL}</p>

                  <label className="block text-sm font-semibold text-gray-800 mb-2.5">Session type</label>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <OptionTile active={mode === "live"} onClick={() => setMode("live")} title="Live" subtitle="Pandit joins on video call" />
                    <OptionTile active={mode === "recorded"} onClick={() => setMode("recorded")} title="Recorded" subtitle="Watch at your own pace" />
                  </div>

                  {mode === "live" && (
                    <>
                      <label className="block text-sm font-semibold text-gray-800 mb-2.5">Session privacy</label>
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        <OptionTile active={sessionPrivacy === "private"} onClick={() => setSessionPrivacy("private")} title="Private" subtitle="Just your family" />
                        <OptionTile active={sessionPrivacy === "public"} onClick={() => setSessionPrivacy("public")} title="Public" subtitle="Shared, priced lower" />
                      </div>
                    </>
                  )}

                  <label className="block text-sm font-semibold text-gray-800 mb-2.5">Language</label>
                  <div className="grid grid-cols-3 gap-3">
                    <OptionTile active={sessionLanguage === "hindi"} onClick={() => setSessionLanguage("hindi")} title="Hindi" />
                    <OptionTile active={sessionLanguage === "marathi"} onClick={() => setSessionLanguage("marathi")} title="Marathi" />
                    <OptionTile active={sessionLanguage === "english"} onClick={() => setSessionLanguage("english")} title="English" />
                  </div>
                </div>
              )}

              {/* STEP 2 — Time slot */}
              {formStep === 2 && (
                <div className="p-8 sm:p-10 bg-[#FDF8F0]">
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-1">Pick a time slot</h2>
                  <p className="text-sm text-gray-500 mb-6">All times shown in {TIMEZONE_LABEL}</p>

                  {mode === "recorded" ? (
                    <p className="text-sm text-gray-600 bg-white rounded-xl border border-[#E9D9BD] p-4">
                      Recorded sessions don't need a fixed time — you can watch it any time on {STHAPANA_DATE_LABEL}.
                    </p>
                  ) : (
                    <>
                      <SlotGroup label="Morning" slots={dateSlots.morning} selectedId={form.timeSlotId} onSelect={(id) => setForm({ ...form, timeSlotId: id })} />
                      <SlotGroup label="Afternoon" slots={dateSlots.afternoon} selectedId={form.timeSlotId} onSelect={(id) => setForm({ ...form, timeSlotId: id })} />

                      {OTHER_FUNCTION_SLOTS.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-[#E9D9BD]">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Other functions (not sthapana)
                          </p>
                          {OTHER_FUNCTION_SLOTS.map((s) => (
                            <div key={s.id} className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-2.5 inline-block">
                              {s.label} · {s.time} {TIMEZONE_LABEL}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* STEP 3 — Your details */}
              {formStep === 3 && (
                <div className="p-8 sm:p-10 bg-[#FDF8F0] space-y-4">
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-1">Your details</h2>
                  <p className="text-sm text-gray-500 mb-4">We'll send a confirmation message to confirm your slot.</p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full name *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      onBlur={() => handleBlur("name")}
                      className={`w-full border rounded-xl p-3 outline-none transition-shadow focus:ring-2 focus:ring-[#C99A4A] focus:border-[#C99A4A] ${
                        touched.name && !nameValid ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {touched.name && !nameValid && <p className="text-xs text-red-500 mt-1">Please enter your name.</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone number *</label>
                    <div className="flex gap-2">
                      <select
                        name="countryCode"
                        value={form.countryCode}
                        onChange={handleChange}
                        className="border border-gray-300 rounded-xl p-3 outline-none focus:ring-2 focus:ring-[#C99A4A] focus:border-[#C99A4A] bg-white"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>{c.label}</option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        onBlur={() => handleBlur("phone")}
                        placeholder="98765 43210"
                        className={`flex-1 border rounded-xl p-3 outline-none transition-shadow focus:ring-2 focus:ring-[#C99A4A] focus:border-[#C99A4A] ${
                          touched.phone && !phoneValid ? "border-red-400" : "border-gray-300"
                        }`}
                      />
                    </div>
                    {touched.phone && !phoneValid && (
                      <p className="text-xs text-red-500 mt-1">Enter a valid phone number — we'll send your confirmation message here.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email (optional)</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-xl p-3 outline-none focus:ring-2 focus:ring-[#C99A4A] focus:border-[#C99A4A]"
                    />
                  </div>
                </div>
              )}

              {/* STEP 4 — Summary & pay */}
              {formStep === 4 && (
                <div className="p-8 sm:p-10 bg-[#FDF8F0]">
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Review & confirm</h2>
                  <SummaryPanel
                    mode={mode}
                    sessionPrivacy={sessionPrivacy}
                    sessionLanguage={sessionLanguage}
                    dateLabel={STHAPANA_DATE_LABEL}
                    selectedSlot={selectedSlot}
                    timezoneLabel={TIMEZONE_LABEL}
                    holdFeeDisplay={holdFeeDisplay}
                    fullPriceDisplay={fullPriceDisplay}
                    expanded
                  />
                </div>
              )}

              {error && <p className="text-red-500 text-sm text-center px-8 pb-2">{error}</p>}

              {/* Nav / sticky submit */}
              <div className="sticky bottom-0 bg-white border-t border-[#E9D9BD] p-5 flex items-center justify-between gap-3">
                {formStep > 1 ? (
                  <button onClick={() => goToStep(formStep - 1)} className="text-sm font-medium text-gray-500 hover:text-gray-700">
                    Back
                  </button>
                ) : <span />}

                {formStep < 4 ? (
                  <button
                    onClick={() => goToStep(formStep + 1)}
                    className="bg-[#8A2432] hover:bg-[#701D28] text-white px-7 py-3 rounded-full font-semibold transition-all"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    onClick={handleFinalSubmit}
                    disabled={loading}
                    className="bg-[#8A2432] hover:bg-[#701D28] text-white px-7 py-3 rounded-full font-bold shadow-lg shadow-[#8A2432]/20 transition-all disabled:opacity-50"
                  >
                    {loading ? "Processing…" : `Pay ${holdFeeDisplay} & confirm`}
                  </button>
                )}
              </div>
            </div>

            {/* Persistent summary panel — visible throughout the form on larger screens */}
            {formStep < 4 && (
              <div className="hidden md:block mt-4">
                <SummaryPanel
                  mode={mode}
                  sessionPrivacy={sessionPrivacy}
                  sessionLanguage={sessionLanguage}
                  dateLabel={STHAPANA_DATE_LABEL}
                  selectedSlot={selectedSlot}
                  timezoneLabel={TIMEZONE_LABEL}
                  holdFeeDisplay={holdFeeDisplay}
                  fullPriceDisplay={fullPriceDisplay}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ---------- FOOTER ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-14 border-t border-[#E9D9BD]">
        <p className="text-sm text-gray-500 max-w-lg leading-relaxed mx-auto text-center md:mx-0 md:text-left">
          {holdFeeDisplay} confirms your slot and includes your materials checklist; full session
          price is shown before you pay anything further.
          {showINR && (
            <>
              <br className="hidden md:block" />
              बुकिंग राशि ₹{HOLD_FEE_INR} आपकी स्लॉट पुष्टि के लिए है और इसमें सामग्री सूची शामिल है।
            </>
          )}
        </p>
      </section>

      {/* Floating "Book" access — visible on desktop and mobile, and stays
          visible even after the form is open (it just scrolls you to it
          instead of resetting anything), so there's no need to scroll
          back to the top to continue booking. */}
      <AnimatePresence>
        {scrolledPastHero && (
          <>
            {/* Mobile: full-width bottom bar */}
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E9D9BD] p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] md:hidden"
            >
              <button
                onClick={handleFloatingCtaClick}
                className="w-full bg-[#8A2432] hover:bg-[#701D28] text-white py-3.5 rounded-full font-bold shadow-lg"
              >
                {showForm ? "Continue booking ↓" : `Book for ${holdFeeDisplay}`}
              </button>
            </motion.div>

            {/* Desktop: floating pill, bottom-right */}
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="hidden md:block fixed bottom-6 right-6 z-30"
            >
              <button
                onClick={handleFloatingCtaClick}
                className="bg-[#8A2432] hover:bg-[#701D28] text-white px-6 py-3.5 rounded-full font-bold shadow-xl shadow-[#8A2432]/25 transition-all hover:-translate-y-0.5"
              >
                {showForm ? "Continue booking ↓" : `Book for ${holdFeeDisplay}`}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============================================================
   SUBCOMPONENTS
   ============================================================ */

function CurrencyToggle({ showINR, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-[#E9D9BD] bg-white p-0.5 text-sm">
      <button
        onClick={() => onChange("INR")}
        className={`px-3 py-1.5 rounded-full font-medium transition-colors ${showINR ? "bg-[#F6E3C4] text-[#8A5A1F]" : "text-gray-500"}`}
      >
        ₹ INR
      </button>
      <button
        onClick={() => onChange("USD")}
        className={`px-3 py-1.5 rounded-full font-medium transition-colors ${!showINR ? "bg-[#F6E3C4] text-[#8A5A1F]" : "text-gray-500"}`}
      >
        $ USD
      </button>
    </div>
  );
}

function SessionCompareCard({ active, onClick, title, price, img, pros, cons }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-3xl overflow-hidden bg-white transition-all ${
        active ? "ring-2 ring-[#8A2432] shadow-xl" : "ring-1 ring-[#E9D9BD] shadow-sm opacity-90 hover:opacity-100"
      }`}
    >
      <div className="aspect-video bg-[#F6E3C4] relative">
        <img src={img} alt={title} className="w-full h-full object-cover" />
        {active && (
          <span className="absolute top-3 right-3 bg-[#8A2432] text-white text-xs font-semibold px-3 py-1 rounded-full">
            Selected
          </span>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-xl font-display font-bold text-gray-900">{title}</h3>
          <span className="text-sm font-semibold text-[#8A2432]">{price}</span>
        </div>
        <div className="space-y-1.5 mb-3">
          {pros.map((p, i) => (
            <p key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-green-600">+</span>{p}</p>
          ))}
        </div>
        <div className="space-y-1.5">
          {cons.map((c, i) => (
            <p key={i} className="text-sm text-gray-500 flex gap-2"><span className="text-amber-500">–</span>{c}</p>
          ))}
        </div>
      </div>
    </button>
  );
}

function OptionTile({ active, onClick, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border px-4 py-3 transition-all ${
        active
          ? "bg-[#8A2432] border-[#8A2432] text-white shadow-md"
          : "bg-white border-gray-300 text-gray-700 hover:border-[#C99A4A] hover:shadow-sm"
      }`}
    >
      <span className="block text-sm font-semibold">{title}</span>
      {subtitle && (
        <span className={`block text-xs mt-0.5 ${active ? "text-white/80" : "text-gray-500"}`}>{subtitle}</span>
      )}
    </button>
  );
}

function SlotGroup({ label, slots, selectedId, onSelect }) {
  if (!slots.length) return null;
  return (
    <div className="mb-5">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{label}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {slots.map((slot) => (
          <button
            key={slot.id}
            type="button"
            onClick={() => onSelect(slot.id)}
            className={`relative text-left text-sm py-3 px-3 rounded-xl border font-medium transition-all ${
              selectedId === slot.id
                ? "bg-[#8A2432] border-[#8A2432] text-white shadow-md"
                : "bg-white border-gray-300 text-gray-700 hover:border-[#C99A4A]"
            }`}
          >
            {slot.recommended && (
              <span className="absolute -top-2 left-2 bg-[#F0B25C] text-[#5A3A0F] text-[10px] font-bold px-2 py-0.5 rounded-full">
                Recommended
              </span>
            )}
            <span className="block">{slot.time}</span>
            <span className={`block text-xs mt-0.5 ${selectedId === slot.id ? "text-white/80" : "text-gray-500"}`}>
              {slot.availableCount} slots left
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SummaryPanel({ mode, sessionPrivacy, sessionLanguage, dateLabel, selectedSlot, timezoneLabel, holdFeeDisplay, fullPriceDisplay, expanded }) {
  return (
    <div className={`bg-white rounded-2xl border border-[#E9D9BD] p-5 ${expanded ? "" : "shadow-sm"}`}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Your booking</p>
      <dl className="space-y-2 text-sm">
        <Row label="Date" value={dateLabel} />
        <Row label="Session" value={`${mode === "live" ? "Live" : "Recorded"}${sessionPrivacy ? ` · ${sessionPrivacy}` : ""}`} />
        <Row label="Language" value={sessionLanguage[0].toUpperCase() + sessionLanguage.slice(1)} />
        {mode === "live" && (
          <Row label="Time" value={selectedSlot ? `${selectedSlot.time} (${timezoneLabel})` : "Not selected yet"} />
        )}
      </dl>
      <div className="border-t border-[#E9D9BD] mt-3 pt-3 space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Slot-hold fee (pay now)</span>
          <span className="font-semibold text-gray-900">{holdFeeDisplay}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Full session price</span>
          <span className="font-semibold text-gray-900">{fullPriceDisplay}</span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-800 font-medium text-right">{value}</dd>
    </div>
  );
}

function StepIndicator({ currentStep, totalSteps, labels }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: totalSteps }).map((_, i) => {
        const step = i + 1;
        const active = step === currentStep;
        const done = step < currentStep;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  active ? "bg-[#8A2432] text-white" : done ? "bg-[#F0B25C] text-white" : "bg-gray-200 text-gray-500"
                }`}
              >
                {done ? "✓" : step}
              </div>
              <span className={`text-[11px] ${active ? "text-gray-800 font-medium" : "text-gray-400"}`}>{labels[i]}</span>
            </div>
            {step < totalSteps && <div className={`w-8 h-0.5 mb-4 ${done ? "bg-[#F0B25C]" : "bg-gray-200"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FaqAccordionItem({ item, isOpen, onToggle }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E9D9BD] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left px-6 py-4 font-medium text-gray-900 hover:bg-[#FDF8F0] transition-colors"
      >
        {item.q}
        <span className={`text-[#8A2432] transition-transform duration-200 ${isOpen ? "rotate-45" : ""}`}>+</span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed">{item.a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StarRating({ value, size = 15 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i < Math.round(value) ? "#F0B25C" : "#E5DAC5"}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function DiyaIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M12 2C12 2 9 6 9 8.5C9 10.2 10.3 11.5 12 11.5C13.7 11.5 15 10.2 15 8.5C15 6 12 2 12 2Z" fill="#8A2432" />
      <path d="M3 15C3 15 6 20 12 20C18 20 21 15 21 15C21 15 17 17 12 17C7 17 3 15 3 15Z" fill="#8A2432" />
    </svg>
  );
}

function FaCheckDot() {
  return (
    <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-[#F6E3C4] flex items-center justify-center">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
        <path d="M4 12.5L9.5 18L20 6" stroke="#8A2432" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function MandalaFlourish({ className }) {
  return (
    <svg viewBox="0 0 100 100" fill="none" className={className}>
      <circle cx="50" cy="50" r="46" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="50" cy="50" r="30" stroke="currentColor" strokeWidth="1.5" />
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 30 * Math.PI) / 180;
        const x1 = 50 + 30 * Math.cos(angle);
        const y1 = 50 + 30 * Math.sin(angle);
        const x2 = 50 + 46 * Math.cos(angle);
        const y2 = 50 + 46 * Math.sin(angle);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.5" />;
      })}
    </svg>
  );
}

// Faint repeating mandala/diya motif used as a subtle section-divider texture.
function MandalaBackgroundTexture() {
  return (
    <div
      className="pointer-events-none fixed inset-0 -z-10 opacity-[0.035]"
      style={{
        backgroundImage:
          "radial-gradient(circle, #8A2432 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }}
    />
  );
}